import * as React from "react";
import { Suggestion } from "../types";
import { checkIcon, declineIcon, docIcon, goToIcon, infoIcon, refreshIcon } from "../icons";
import "../styles/suggestions.css";

interface SuggestionCardProps {
  suggestion: Suggestion;
  timestamp: string;
  onApply: (id: string) => void;
  onDecline: (id: string) => void;
  onUndo: (id: string) => void;
  onRegenerate: (id: string) => void;
  onGoTo: (id: string) => void;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({ suggestion, timestamp, onApply, onDecline, onUndo, onRegenerate, onGoTo }) => {
  const { id, section, original, revised, reason, status } = suggestion;
  const isAccepted = status === "accepted";
  const isDeclined = status === "declined";
  const [open, setOpen] = React.useState(true);

  const handleGoTo = (event: React.MouseEvent) => {
    event.stopPropagation();
    onGoTo(id);
  };

  return (
    <div className="review-card-wrapper">
      <div className={`suggestion-card ${isAccepted ? "accepted" : ""} ${isDeclined ? "declined" : ""}`}>
        <div
          className="suggestion-header"
          onClick={() => setOpen((prev) => !prev)}
          style={{ cursor: "pointer" }}
          aria-expanded={open}
          role="button"
        >
          <div className="suggestion-header-icon">{docIcon}</div>
          <div className="suggestion-header-text">
            <span className="suggestion-label">Suggested Edit</span>
            <span className="suggestion-title">{section}</span>
          </div>
          <div className="suggestion-header-actions">
            <button
              className="icon-btn btn-go-to"
              onClick={handleGoTo}
              title="Go to this text in Word"
              aria-label="Go to this text in Word"
            >
              {goToIcon}
            </button>
            <div className="suggestion-type-badge">{open ? "AI ▾" : "AI ▸"}</div>
          </div>
        </div>

        <div className={`collapsible ${open ? "" : "closed"}`}>
          <div className="suggestion-body">
            <div className="suggestion-section">
              <div className="section-label original">Current Text</div>
              <div className="suggestion-text original">{original}</div>
            </div>
            <div className="suggestion-section">
              <div className="section-label revised">Proposed Revision</div>
              <div className="suggestion-text revised">{revised}</div>
            </div>
          </div>

          <div className="suggestion-reason">
            <div className="suggestion-reason-icon">{infoIcon}</div>
            <div className="suggestion-reason-text">
              <strong>Why:</strong> {reason}
            </div>
          </div>
        </div>

        <div className="suggestion-actions">
          {status === "pending" ? (
            <>
              <button className="btn btn-apply" onClick={() => onApply(id)}>
                {checkIcon}
                Apply
              </button>
              <button className="btn btn-decline" onClick={() => onDecline(id)}>
                {declineIcon}
                Decline
              </button>
              <button className="btn btn-regenerate" onClick={() => onRegenerate(id)} title="Generate alternative">
                {refreshIcon}
              </button>
            </>
          ) : (
            <div className={`status-badge ${isAccepted ? "accepted" : "declined"}`}>
              {isAccepted ? checkIcon : declineIcon}
              {isAccepted ? "Change Applied" : "Declined"}
              <span className="undo-link" onClick={() => onUndo(id)}>
                Undo
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="message-time">{timestamp}</div>
    </div>
  );
};

export default SuggestionCard;
